import React from "react";
import { Icon } from "@lululemon/ui-library";
import {
  pauseIcon,
  playIcon,
  volumeIcon,
  volumeOffIcon,
} from "@lululemon/ui-icons";
import { ccOn, ccOff } from "./reel-video-controls-icons";
import PropTypes from "prop-types";
import styles from "./reel-video-controls.module.scss";
import { useStoryNavigation } from "../utils/reel-navigation-context";

const preferredCCStateKey = "featureReelsUserPreferredCCState";

const ReelVideoControl = ({
  ariaLabel,
  handleOnClick,
  iconContent,
  ...rest
}) => (
  <button
    aria-label={ariaLabel}
    className={styles.reelVideoControlIcon}
    onClick={handleOnClick}
    {...rest}
  >
    <Icon content={iconContent} />
  </button>
);

const ReelVideoControls = ({ dispatchInstaStoryAction, videoRef }) => {
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [isMuted, setIsMuted] = React.useState(true);
  const [isClosedCaptionsActive, setIsClosedCaptionsActive] =
    React.useState(true);

  const { isStoryLoading, storyIndex } = useStoryNavigation();

  const saveUserPreferredCCState = (newPreferredState) => {
    try {
      window.localStorage.setItem(preferredCCStateKey, newPreferredState);
    } catch (e) {
      console.log("Feature reels | Error trying to save preferred CC state");
    }
  };

  React.useEffect(() => {
    let isUserPrefCCEnabled;
    try {
      isUserPrefCCEnabled = window.localStorage.getItem(preferredCCStateKey);
    } catch (e) {
      console.log("Feature reels | Error trying to get preferred CC state");
    }

    const shouldActivatedClosedCaptions =
      isUserPrefCCEnabled === null ? true : isUserPrefCCEnabled === "true";
    setIsClosedCaptionsActive(shouldActivatedClosedCaptions);
    saveUserPreferredCCState(shouldActivatedClosedCaptions);

    if (videoRef.current?.textTracks?.[0]) {
      videoRef.current.textTracks[0].mode = shouldActivatedClosedCaptions
        ? "showing"
        : "hidden";

      const textCues = videoRef.current.textTracks[0].cues;
      if (textCues) {
        for (let i = 0; i < textCues.length; i++) {
          textCues[i].snapToLines = false;
          textCues[i].line = 75;

          //For force CC to break lines
          const words = textCues[i].text.split(" ");
          if (words.length > 7 && !textCues[i].hasLineBreaks) {
            words.splice(7, 0, "\n");
            textCues[i].text = words.join(" ");
            textCues[i].hasLineBreaks = true;
          }
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, storyIndex]);

  const onPlayButtonClick = async () => {
    if (videoRef.current?.paused) {
      await videoRef.current?.play();
      dispatchInstaStoryAction("play", true);
    } else {
      videoRef.current?.pause();
      dispatchInstaStoryAction("pause", true);
    }

    setIsPlaying(!isPlaying);
  };

  const onVolumeButtonClick = () => {
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  const onCloseCaptionButtonClick = () => {
    const newIsClosedCaptionsActive = !isClosedCaptionsActive;
    setIsClosedCaptionsActive(newIsClosedCaptionsActive);
    saveUserPreferredCCState(newIsClosedCaptionsActive);

    if (!videoRef.current.textTracks.length) return;
    videoRef.current.textTracks[0].mode = newIsClosedCaptionsActive
      ? "showing"
      : "hidden";
  };

  return (
    <div
      data-testid="reels-video-controls"
      className={styles.reelVideoControls}
    >
      <div className={styles.reelVideoControlGroup}>
        <ReelVideoControl
          ariaLabel={isPlaying ? "Pause video" : "Play video"}
          data-testid="reel-control-play"
          disabled={isStoryLoading}
          handleOnClick={onPlayButtonClick}
          iconContent={isPlaying ? pauseIcon : playIcon}
        />
        <ReelVideoControl
          ariaLabel={isMuted ? "Unmute video" : "Mute video"}
          data-testid="reel-control-volume"
          handleOnClick={onVolumeButtonClick}
          iconContent={isMuted ? volumeOffIcon : volumeIcon}
        />
      </div>
      <div className={styles.reelVideoControlGroup}>
        <ReelVideoControl
          ariaLabel={
            isClosedCaptionsActive
              ? "Disable closed captions"
              : "Enable closed captions"
          }
          data-testid="reel-control-CC"
          handleOnClick={onCloseCaptionButtonClick}
          iconContent={isClosedCaptionsActive ? ccOn : ccOff}
        />
      </div>
    </div>
  );
};

ReelVideoControls.propTypes = {
  dispatchInstaStoryAction: PropTypes.func.isRequired,
  videoRef: PropTypes.object.isRequired,
};

export default ReelVideoControls;
